// Imports
import React from 'react';
import styled from 'styled-components/macro';
import theme from '../../../../../../shareables/theme';
import { faHome, faPhone } from '@fortawesome/pro-regular-svg-icons';
import formatPhoneNumber from '../../../../../../shareables/utils/format-phone-number';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserAvatar from '../../../../shared-components/user-avatar';
import { useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import { GetPetsResponse } from '../../../../../../shareables/types/api/main/website/page/type/208/pets/get';


// Styled components
const Wrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 1.25rem;
	
	background: #fafafa;
	border: 0.0625rem solid #eee;
	border-radius: 0.1875rem;
	padding: 1rem;
	
	&:not(:last-child) {
		margin-bottom: 1rem;
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		flex-direction: column;
		gap: 0.5rem;
		text-align: center;
		margin-left: 0;
	}
`;

const Container = styled.div`
	break-inside: avoid;
`;

const Header = styled.h3`
	display: inline-block;
	margin-bottom: 0.5rem;
	line-height: 1;
	font-weight: 400;
	font-size: 1.4rem;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		text-align: center;
	}
	
	&:last-child {
		margin-bottom: 0 !important;
	}
`;

const ProfileAvatar = styled(UserAvatar)`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 3rem;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		font-size: 2rem;
	}
`;

const ProfilePhotoArea = styled.div`
	grid-area: 1 / 1 / 3 / 2;
	display: flex;
	height: 100%;
	align-items: center;
	justify-content: center;
	
	img,
	${ProfileAvatar} {
		width: 6.25rem;
		height: 6.25rem;
		border-radius: 50%;
		box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
		
		@media (max-width: ${theme.breakpoints.mobile}px) {
			width: 4rem;
			height: 4rem;
		}
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		grid-area: 1 / 1 / 1 / 1;
	}
`;

const OwnerNameContainer = styled.div`
	&:not(:last-child) {
		margin-bottom: 0.5rem;
	}
`;

const OwnerDetails = styled.div`
	display: flex;
	gap: 1rem;
	
	a {
		color: inherit;
		text-decoration: none;
		
		& :hover,
		:focus {
			color: inherit;
		}
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		flex-direction: column;
		gap: 0;
	}
`;


// Define the accepted props
interface Props {
	pet: GetPetsResponse[0];
}


// Function component
const Pet: React.FC<Props> = ({ pet }) => {
	// Use Redux functionality
	const website = useAppSelector((state) => state.self.website);
	
	
	// Return JSX
	return (
		<Wrapper>
			<ProfilePhotoArea>
				{pet.profilePhotoURL ? (
					<img src={pet.profilePhotoURL} alt='Profile avatar' />
				) : (
					<ProfileAvatar firstName={pet.name} />
				)}
			</ProfilePhotoArea>
			
			<Container>
				<Header>{pet.name}</Header>
				
				{typeof pet.age === 'number' && (
					<React.Fragment>
						{' '}
						<span className='is-gray'>
							<em>{pet.age ? `${pet.age} year${pet.age > 1 ? 's' : ''} old` : 'Less than a year old'}</em>
						</span>
					</React.Fragment>
				)}
				
				<OwnerNameContainer>
					Belongs to <strong>{pet.owner.name}</strong>
				</OwnerNameContainer>
				
				{(pet.owner.address || pet.owner.phone) && (
					<OwnerDetails>
						{pet.owner.phone && (
							<div>
								<FontAwesomeIcon icon={faPhone} />{' '}
								<a href={`tel:${pet.owner.phone}`} target='_blank' rel='noopener noreferrer'>
									{formatPhoneNumber(pet.owner.phone, website?.country.callingCode ?? 1)}
								</a>
							</div>
						)}
						{pet.owner.address && (
							<div>
								<FontAwesomeIcon icon={faHome} /> <span>{pet.owner.address}</span>
							</div>
						)}
					</OwnerDetails>
				)}
			</Container>
		</Wrapper>
	);
};

export default Pet;
